import * as s from "./CardItemStyled";
import { FaArrowCircleDown, FaArrowCircleUp } from "react-icons/fa";
import { INDECREASECOLORS } from "styles/colors";
import LineChart from "components/atoms/chart/LineChart";
import {
  RecorderKeyType,
  WidgetCardDetail,
  WidgetCardNoMap,
  WidgetCardResult,
} from "api/interfaces/widgetInterface.interface";
import CardItemDetail from "./CardItemDetail";
import { changeFirstWordToUppercase, formatNumber } from "utils/functions";
import { UnderlineText } from "components/atoms/text/Text";
import { Datum } from "@nivo/line";
import { FaCircleMinus } from "react-icons/fa6";
import { useMemo, useState } from "react";
import { IconType } from "react-icons";
interface Props {
  name: CardItemName;
  cardData: WidgetCardResult;
  isDetailView: boolean;
  onView: () => void;
}
export type CardItemName =
  | "recorderCount"
  | "cameraCount"
  | "alertCount"
  | "organizationCount";

const keyTitle: { [key in CardItemName]: string } = {
  recorderCount: "Recorders",
  cameraCount: "Cameras",
  alertCount: "Alerts",
  organizationCount: "Organizations",
};

export type IncreaseString = "INCREASE" | "DECREASE" | "NONE";

const vmaxTypes = ["VMAX-IP", "VMAX-A1"];

export const changeStringBottom = (string: string) => {
  switch (string) {
    case "CP":
      return "Sub Channel Partner";
    case "EU":
      return "End User";
    default:
      return changeFirstWordToUppercase(string);
  }
};

const getTypeKey = (type: string): keyof WidgetCardNoMap => {
  if (type.includes("VMAX")) {
    return type.replace("VMAX-", "vmax") as keyof WidgetCardNoMap;
  }
  return type.toLowerCase() as keyof WidgetCardNoMap;
};

export default function CardItem(props: Props): JSX.Element {
  // console.log(props.cardData, props.name);

  const [recorderType, setRecorderType] = useState<string | null>(null);

  const transformData = (data: WidgetCardDetail[]): WidgetCardDetail[] => {
    let vmaxCount = 0;
    const items: Record<string, WidgetCardDetail[]> = {
      SPECTRUM: [],
      COVA: [],
    };

    data.forEach((item) => {
      const type = item.type.toUpperCase();
      if (type === "SPECTRUM" || type === "COVA") {
        items[type].push({ ...item, type });
      } else {
        vmaxCount += item.count;
      }
    });

    return [
      ...items.SPECTRUM,
      ...items.COVA,
      { type: "VMAX", count: vmaxCount },
    ];
  };

  const calculateTotalIncrease = (
    data: Datum[]
  ): {
    color: string;
    Icon: IconType;
  } => {
    const none = { color: INDECREASECOLORS.NONE, Icon: FaCircleMinus };
    // 데이터가 2개 이상 있는지 확인
    if (data.length < 2) {
      return none;
    }

    const firstY = data[0].y as number;
    const lastY = data[data.length - 1].y as number;

    // 첫 번째와 마지막 데이터가 모두 null이 아닌지 확인
    if (firstY === null || lastY === null) {
      return none;
    }

    // 첫 번째 값과 마지막 값을 비교하여 결과 반환
    if (lastY > firstY) {
      return {
        color:
          props.name === "alertCount"
            ? INDECREASECOLORS.DECREASE
            : INDECREASECOLORS.INCREASE,
        Icon: FaArrowCircleUp,
      };
    } else if (lastY < firstY) {
      return {
        color:
          props.name === "alertCount"
            ? INDECREASECOLORS.INCREASE
            : INDECREASECOLORS.DECREASE,
        Icon: FaArrowCircleDown,
      };
    } else {
      return none;
    }
  };

  const { color, Icon } = calculateTotalIncrease(props.cardData.stats);

  return (
    <s.Item>
      <s.Top className="content ">
        <Title
          isDetail={props.isDetailView}
          onView={props.onView}
          label={keyTitle[props.name as CardItemName]}
        />

        {props.isDetailView ? (
          <CardItemDetail
            name={props.name as CardItemName}
            details={props.cardData.details}
          />
        ) : (
          <>
            <s.Increase color={color}>
              <span className="number bold">
                {formatNumber(props.cardData.count)}
              </span>
              <Icon size={16} />
            </s.Increase>
            <s.ChartWrapper>
              <LineChart
                data={[
                  {
                    id: "summary-increase",
                    color: color,
                    data: props.cardData.stats,
                  },
                ]}
                yScale={{
                  type: "linear",
                  min: 0,
                  max: "auto",
                  stacked: true,
                  reverse: false,
                }}
                curve={"catmullRom"}
                enablePoints={false}
                margin={{ top: 5, right: 0, bottom: 0, left: 0 }}
                enableGridY={false}
                lineWidth={3}
                enableArea={true}
                isInteractive={false}
                axisBottom={null}
              />
            </s.ChartWrapper>
          </>
        )}
      </s.Top>

      {/* card 클릭 시 detail View */}
      {!props.isDetailView && props.cardData.details && (
        <s.Bottom className="content ">
          <s.BottomInner>
            {props.name === "recorderCount" || props.name === "cameraCount"
              ? transformData(props.cardData.details).map(
                  (item: WidgetCardDetail, index: number) => (
                    <CardTypeSection
                      item={item}
                      index={index}
                      name={props.name}
                      key={item.type}
                      cardData={props.cardData}
                      setRecorderType={setRecorderType}
                    />
                  )
                )
              : props.cardData.details.map(
                  (item: WidgetCardDetail, index: number) => (
                    <s.DetaiItem key={index}>
                      <span className="detail-title">
                        {changeStringBottom(item.type)}
                      </span>
                      <span className="detail-value">
                        {formatNumber(item.count)}
                      </span>
                    </s.DetaiItem>
                  )
                )}
          </s.BottomInner>
        </s.Bottom>
      )}

      {props.name === "recorderCount" && recorderType && (
        <s.RecorderType>
          <s.Top className="content">
            <Title
              isDetail={true}
              onView={() => setRecorderType(null)}
              label={recorderType}
            />

            <CardItemDetail
              name={props.name as CardItemName}
              isType
              details={
                (props.cardData[
                  getTypeKey(recorderType || "")
                ] as WidgetCardDetail[]) || []
              }
            />
          </s.Top>
        </s.RecorderType>
      )}
    </s.Item>
  );
}

const Title = ({
  isDetail,
  onView,
  label,
}: {
  isDetail: boolean;
  onView: () => void;
  label: string;
}) => {
  return (
    <s.Title isDetail={isDetail} className="drag-handle">
      <div className="name bold">{label}</div>
      <UnderlineText
        className="detail"
        bold
        onClick={onView}
        data-testid="dashboard-dashboard-card-underline-text-view"
      >
        {isDetail ? "Graph" : "Details"}
      </UnderlineText>
    </s.Title>
  );
};

const CardTypeSection = ({
  item,
  index,
  name,
  cardData,
  setRecorderType,
}: {
  item: WidgetCardDetail;
  index: number;
  name: CardItemName;
  cardData: WidgetCardResult;
  setRecorderType: (type: string) => void;
}) => {
  const isRecorder = name === "recorderCount" && item.count > 0;
  const isVmax = item.type === "VMAX" && isRecorder;
  const vmaxList = useMemo(
    () =>
      vmaxTypes.filter(
        (type) =>
          (cardData?.[getTypeKey(type) as RecorderKeyType] || []).length > 0
      ),
    [cardData]
  );
  return (
    <s.DetaiItem
      key={index}
      isVmax={isVmax}
      isRecorder={isRecorder}
      onClick={() => {
        if (isRecorder && item.type !== "VMAX") {
          setRecorderType(item.type);
        }
      }}
      data-testid="dashboard-dashboard-card-detai-item-set-recorder-type"
    >
      {isVmax && (
        <div className="links">
          {vmaxList.map((type) => (
            <span
              key={type}
              onClick={() => setRecorderType(type)}
              className="detail-title"
              data-testid="dashboard-dashboard-card-span-set-recorder-type"
            >
              {type}
            </span>
          ))}
        </div>
      )}

      <span className="detail-title">{changeStringBottom(item.type)}</span>
      <span className="detail-value">{formatNumber(item.count)}</span>
    </s.DetaiItem>
  );
};
